import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    userName: state.context.session.userName,
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isLoggedIN: state.context.session.isAuthenticated,
    discrepancyStore: state.KGPState.DiscrepancyStore,
    parameters: state.KGPState.DiscrepancyStore.discrepancyCollectionParams,
    location: getLocation(state),
    featurePages: state.context.session.properties.featurePages,
    isSecurityRolesAssigned: state.context.session?.properties?.isSecurityRolesAssigned?.toLowerCase() === "true",
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    loadDiscrepancyCollection,
};

export interface DiscrepancyHistoryStyles {
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
}

export const discrepancyHistoryStyles: DiscrepancyHistoryStyles = {
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
};

const styles = discrepancyHistoryStyles;

const DiscrepancyHistoryContainer: React.FC<Props> = ({
    id,
    userName,
    signInUrl,
    currentUserIsGuest,
    history,
    discrepancyStore,
    loadDiscrepancyCollection,
    parameters,
    location,
    featurePages,
    isSecurityRolesAssigned,
}) => {
    useEffect(() => {
        if (!(userName && !currentUserIsGuest)) {
            history.push(`${signInUrl}`);
        }
    }, [userName, currentUserIsGuest]);

    useEffect(() => {
        if (!discrepancyStore.isLoading && !discrepancyStore.discrepancyCollection && userName && !currentUserIsGuest) {
            loadDiscrepancyCollection({ ...parameters });
        }
    }, [discrepancyStore, userName, currentUserIsGuest]);

    if (featurePages?.includes("Discrepancy History")) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="no-access-message">
                    {translate(`You don’t have permission to view this page`)}
                </Typography>
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(DiscrepancyHistoryContainer)),
    definition: {
        group: "Common",
        displayName: "DiscrepancyHistoryContainer",
        fieldDefinitions: [],
    },
};

export default widgetModule;
